/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import Invoice from '../../../models/invoiceModel';
import History from "../../../models/historyModel";
import {setNavigationParam} from "../../../util/navigateUtil";
import {sendGaPageView} from "../../../util/analytics";
import {downloadFile, navigate, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import InvoiceDetails from "./invoiceDetails";
import {onInvoicePreview, onSetCrumbs} from "../../../util/events";
import {InvoiceType} from "../../../util/interfaces";
import {downloadInvoice} from "../../../util/requestSender";
import {AppContext} from "../../misc/appContext";
import {createErrorMessage} from "../../../util/utils";
import {CREDIT_DATA_ROUTE, EDIT_INVOICE_ROUTE} from "../../../util/constants";

export default function InvoiceDetailsController() {
    const context = useContext(AppContext);
    const [invoice, setInvoice] = useState(new Invoice());
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [loadingInvoice, setLoadingInvoice] = useState(true);
    const [history, setHistory] = useState<Array<History>>([]);

    function onClose() {
        window.history.back();
    }

    function onClickEdit() {
        if (invoice.invoiceType === InvoiceType.DEBET) {
            setNavigationParam(invoice);
            navigate(EDIT_INVOICE_ROUTE);
        } else navigate(`${CREDIT_DATA_ROUTE}?invoice-id=${invoice.referenceInvoiceId}`);
    }

    function onClickPreview() {
        setLoadingHistory(true);
        downloadInvoice({
            invoice_id: invoice.id,
            account_id: invoice.accountId,
            noHistory: true,
            callback: (data: ResponseData, response: Response) => {
                setLoadingHistory(false);
                if (data.status === 'success') {
                    // FILE RESPONSE
                    response.blob().then(blob => {
                        onInvoicePreview(URL.createObjectURL(blob));
                    });
                } else if (!data.handled) {
                    showDialog(createErrorMessage(data.message ?? 'Het downloaden van de factuur is niet gelukt.'),
                        'Downloaden mislukt');
                }
            }
        });
    }

    function onClickDownload() {
        setLoadingHistory(true);
        downloadInvoice({
            invoice_id: invoice.id,
            account_id: invoice.accountId,
            callback: (data: ResponseData, response: Response) => {
                setLoadingHistory(false);
                if (data.status === 'success') {
                    // FILE RESPONSE
                    const header = response.headers.get('Content-Disposition');
                    let fileName = `${invoice.reference}.pdf`;
                    if (header)
                        fileName =
                            header.substring(header.indexOf('filename=') + 'filename='.length, header.length);
                    response.blob().then(blob => {
                        downloadFile(blob, fileName);
                    });
                } else if (!data.handled) {
                    showDialog(createErrorMessage(data.message ?? 'Het downloaden van de factuur is niet gelukt.'),
                        'Downloaden mislukt');
                }
            }
        });
    }

    function onClickSend() {
        showSnackbar(`De factuur wordt verstuurd...`);
        invoice.send((invoice: Invoice, success: boolean, data: ResponseData) => {
            if (success) {
                setInvoice(invoice);
                loadHistory(invoice);
                showSnackbar('De factuur is succesvol verstuurd');
            } else if (!data.handled)
                showDialog(createErrorMessage(data.message ?? 'Er is iets misgegaan bij het verzenden van de factuur.'),
                    'Verzenden mislukt');
        });
    }

    function loadHistory(invoice: Invoice) {
        if (!invoice.id) return;
        setLoadingHistory(true);
        History.getAll(invoice, (history: History[]) => {
            setLoadingHistory(false);
            setHistory(history);
        });
    }

    function getInvoice() {
        const invoiceId = new URLSearchParams(location.search).get('id')
        const {account} = context.accountContext;
        if (!invoiceId || !account) return window.history.back();
        setLoadingInvoice(true);
        Invoice.get(invoiceId, account.id, (invoice: Invoice | null) => {
            setLoadingInvoice(false);
            if (!invoice) return window.history.back();
            setInitial(invoice);
        });
    }

    function setInitial(invoice: Invoice) {
        sendGaPageView('/factuur-details');
        onSetCrumbs([{label: 'Facturen', route: '/facturen'}, {
            label: `Order ${invoice.orderId}`,
            route: '/factuur-details'
        }]);
        setInvoice(invoice);
        loadHistory(invoice);
    }

    useEffect(() => {
        getInvoice();
    }, []);

    return (
        <InvoiceDetails onClickEdit={onClickEdit} onClose={onClose} onClickDownload={onClickDownload}
                        onClickSend={onClickSend} onClickPreview={onClickPreview} invoice={invoice} history={history}
                        loadingHistory={loadingHistory} loadingInvoice={loadingInvoice}/>
    );
}